// Camphor — Factory Files documentary overlay composition.
// Manually edited per Yash 2026-06-07: BigStat = number-only, danda removed,
// most colors white, EquationStack drops → BarCompare chart, channel CTAs in,
// stock "subscribe" callout removed, custom SUBSCRIBE pill covers misspelled
// red pill burned into base @ 647.4–652.4s.
// composition: CamphorFullPreview · 16fps · 1280×704

import React from "react";
import { AbsoluteFill, Sequence } from "remotion";
import { PaletteProvider, BarCompare, BigStat, Tag, TextHighlight } from "./overlayKit";
import { CAMPHORFULLPREVIEW_PALETTE } from "./CamphorFullPreviewPalette";
import { CTASubscribeBell } from "./cta/CTASubscribeBell";
import { CTACombinedBar } from "./cta/CTACombinedBar";

const FPS = 16;
const f = (s: number) => Math.round(s * FPS);

// Hand-crafted SUBSCRIBE pill that covers the burned-in misspelled "SUSCCRSIIOE"
// at lower-right of the base, visible 647.4–652.4s. Sized larger than the source
// pill to fully occlude it; styled to read as the channel's real CTA.
const SubscribeCover: React.FC = () => (
  <AbsoluteFill>
    <div
      style={{
        position: "absolute",
        left: 660,           // covers ~52%-92% horizontal (source pill ≈ 55%-85%)
        top: 478,            // covers ~68%-86% vertical
        width: 520,
        height: 128,
        background: "#cc0000",
        borderRadius: 999,
        boxShadow: "0 6px 28px rgba(0,0,0,0.55), 0 0 0 2px rgba(255,255,255,0.08) inset",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 18,
        color: "#ffffff",
        fontFamily: "Inter, sans-serif",
        fontWeight: 900,
        fontSize: 50,
        letterSpacing: 2,
        textShadow: "0 2px 8px rgba(0,0,0,0.6)",
      }}
    >
      <span style={{ fontSize: 44, lineHeight: 1, transform: "translateY(2px)" }}>▶</span>
      SUBSCRIBE
    </div>
  </AbsoluteFill>
);

export const CamphorFullPreview: React.FC = () => (
  <PaletteProvider value={CAMPHORFULLPREVIEW_PALETTE}>
    <AbsoluteFill>

      {/* Channel logo is composited at the ffmpeg stage by modal_factory_render.py
          (spec.add_logo + logo_url + logo_width=60). Do NOT bake it into the comp. */}

      {/* 00:36 — 5000 PPM (sulfur in paper-mill waste). Number only. */}
      <BigStat value={5000} suffix=" PPM" valueS={34.192} decimals={0} outS={36.656} />

      {/* 00:42 — channel-crafted Like+Subscribe CTA (replaces "WATCH TILL END") */}
      <Sequence from={f(42.586)} durationInFrames={f(4.0)} layout="none">
        <CTASubscribeBell channel="FACTORY PROCESS DECODER" />
      </Sequence>

      {/* 01:07 — 3% MAX (US FDA skin-products legal limit). Number only. */}
      <BigStat value={3} suffix="% MAX" valueS={67.431} decimals={1} outS={69.802} />

      {/* 01:37 — narration touches subscribing → channel-crafted CTA. */}
      <Sequence from={f(97.0)} durationInFrames={f(4.0)} layout="none">
        <CTASubscribeBell channel="FACTORY PROCESS DECODER" />
      </Sequence>

      {/* 02:00 — CINNAMOMUM CAMPHORA. Name only, white. */}
      <Tag inS={118.95} outS={121.879} label="CINNAMOMUM CAMPHORA" color="primary" />

      {/* 02:27 — 80% (old camphor consumed by film/celluloid industry). */}
      <BigStat value={80} suffix="%" valueS={147.186} decimals={0} outS={149.651} />

      {/* 02:35 — 30% (Japan/Taiwan annual revenue from camphor). */}
      <BigStat value={30} suffix="%" valueS={155.719} decimals={0} outS={158.312} />

      {/* 02:49 — GUSTAF KOMPPA · 1901. Name only, white. */}
      <Tag inS={169.211} outS={171.617} label="GUSTAF KOMPPA · 1901" color="primary" />

      {/* 03:29 — SAPTAGIR CAMPHOR · HYDERABAD. Name only, white. */}
      <Tag inS={209.454} outS={212.081} label="SAPTAGIR CAMPHOR · HYDERABAD" color="primary" />

      {/* 03:40 — 20 K MT plant capacity (not 20,000). Number only. */}
      <BigStat value={20} suffix=" K MT" valueS={220.647} decimals={0} outS={223.203} />

      {/* 04:18 — DISTILLATION step name. White, no danda. */}
      <Tag inS={258.762} outS={260.067} label="DISTILLATION" color="primary" />

      {/* 04:33 — 5000 PPM → < 5 PPM dramatic drop (chart, not text). */}
      <BarCompare
        v1={5000} v2={5}
        v1Label="5000 PPM" v2Label="< 5 PPM"
        l1S={273.135} l2S={276.096} outS={278.235}
      />

      {/* 04:43 — 155°C distillation boil temp. Number only. */}
      <BigStat value={155} suffix=" °C" valueS={283.897} decimals={0} outS={286.478} />

      {/* 04:58 — ISOMERIZATION step name. */}
      <Tag inS={298.457} outS={300.203} label="ISOMERIZATION" color="primary" />

      {/* 05:14 — 165°C TiO₂ catalyst temp. Number only. */}
      <BigStat value={165} suffix=" °C" valueS={314.327} decimals={0} outS={316.49} />

      {/* 05:26 — ESTERIFICATION step name. */}
      <Tag inS={326.026} outS={328.469} label="ESTERIFICATION" color="primary" />

      {/* 05:35 — 25°C esterification locked temp. Number only. */}
      <BigStat value={25} suffix=" °C" valueS={335.233} decimals={0} outS={337.686} />

      {/* 05:58 — 97% PURITY (the only beat kept from the −0.09 MPa equation). */}
      <BigStat value={97} suffix="% PURITY" valueS={358.128} decimals={0} outS={360.848} />

      {/* 06:10 — SAPONIFICATION step name. */}
      <Tag inS={370.579} outS={372.709} label="SAPONIFICATION" color="primary" />

      {/* 06:19 — 100°C saponification boil. Number only. */}
      <BigStat value={100} suffix=" °C" valueS={379.205} decimals={0} outS={381.414} />

      {/* 06:24 — ISOBORNEOL name only. */}
      <Tag inS={384.418} outS={387.079} label="ISOBORNEOL" color="primary" />

      {/* 06:35 — channel-crafted LIKE·SHARE·SUBSCRIBE bar (replaces "SUBSCRIBE & LIKE"). */}
      <Sequence from={f(395.431)} durationInFrames={f(4.5)} layout="none">
        <CTACombinedBar />
      </Sequence>

      {/* 06:42 — DEHYDROGENATION step name. */}
      <Tag inS={402.476} outS={405.059} label="DEHYDROGENATION" color="primary" />

      {/* 06:53 — 300°C dehydrogenation temp. Number only. */}
      <BigStat value={300} suffix=" °C" valueS={413.007} decimals={0} outS={415.228} />

      {/* 07:15 — CRYSTALLIZATION + SUBLIMATION. White, sub dropped. */}
      <Tag inS={435.328} outS={438.292} label="CRYSTALLIZATION + SUBLIMATION" color="primary" />

      {/* 08:04 — YELLOW PHENYL (keeps the literal yellow accent — real yellow phenyl). */}
      <TextHighlight hi="YELLOW PHENYL" hiS={484.565} pre="" post="" hiColor="accent_gold" outS={486.914} />

      {/* 08:45 — OPTICAL ROTATION step name only. */}
      <Tag inS={525.119} outS={527.595} label="OPTICAL ROTATION" color="primary" />

      {/* 10:47–10:53 — custom SUBSCRIBE pill covering the misspelled burned-in pill. */}
      <Sequence from={f(647.0)} durationInFrames={f(6.2)} layout="none">
        <SubscribeCover />
      </Sequence>

    </AbsoluteFill>
  </PaletteProvider>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
